import React from "react";
import { Mountain, MessageCircle, Camera, Compass, Bookmark, Calendar, CheckCircle2, Trophy } from "lucide-react";

const ICONS = { Mountain, MessageCircle, Camera, Compass, Bookmark };

/**
 * Renders the active weekly + monthly challenges with progress bars.
 * `items` is the array from GET /api/gamification/challenges.
 */
export default function WeeklyChallenges({ items, compact = false }) {
    if (!items?.length) {
        return (
            <div data-testid="challenges-empty" className="bg-snowcap border border-ridge-line rounded-xl p-6 text-center">
                <Trophy className="w-10 h-10 text-slate-gray mx-auto mb-2" />
                <p className="text-sm text-slate-gray">No active challenges right now. Check back Monday morning.</p>
            </div>
        );
    }

    const weekly = items.filter((c) => c.kind === "weekly");
    const monthly = items.filter((c) => c.kind === "monthly");

    return (
        <div data-testid="challenges-list" className="space-y-6">
            {weekly.length > 0 && (
                <Group title="This week" tagline="Resets every Monday." items={weekly} compact={compact} testid="weekly" />
            )}
            {monthly.length > 0 && (
                <Group title="This month" tagline="Bigger XP rewards for sustained explorers." items={monthly} compact={compact} testid="monthly" />
            )}
        </div>
    );
}

function Group({ title, tagline, items, compact, testid }) {
    return (
        <section data-testid={`challenges-${testid}`}>
            <header className="flex items-center justify-between mb-3">
                <div>
                    <h3 className="font-display font-bold text-lg text-midnight-pine flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-ember-orange" /> {title}
                    </h3>
                    <p className="text-xs text-slate-gray">{tagline}</p>
                </div>
            </header>
            <div className={`grid gap-3 ${compact ? "grid-cols-1" : "sm:grid-cols-2 lg:grid-cols-3"}`}>
                {items.map((c) => <ChallengeCard key={c.id} c={c} />)}
            </div>
        </section>
    );
}

function ChallengeCard({ c }) {
    const Icon = ICONS[c.icon] || Trophy;
    const progress = c.progress || 0;
    const target = c.target_count || 1;
    const pct = Math.min(100, Math.round((progress / target) * 100));
    const done = !!c.completed_at;
    return (
        <div
            data-testid={`challenge-${c.slug}`}
            className={`relative bg-snowcap border rounded-xl p-4 transition-all ${
                done ? "border-moss/50 bg-moss/5" : "border-ridge-line hover:border-ember-orange"
            }`}
        >
            {done && (
                <div className="absolute top-3 right-3">
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-moss text-white text-[10px] font-bold uppercase tracking-widest">
                        <CheckCircle2 className="w-3 h-3" /> Done
                    </span>
                </div>
            )}
            <div className="flex items-center gap-3 mb-2">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${
                    done ? "bg-moss text-white" : "bg-ember-orange/15 text-ember-orange"
                }`}>
                    <Icon className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="font-display font-bold text-base text-midnight-pine leading-tight">{c.title}</div>
                    <div className="text-xs text-slate-gray">{c.description}</div>
                </div>
            </div>
            <div className="h-2 bg-trail-tan/60 rounded-full overflow-hidden mt-2">
                <div className={`h-full transition-all ${done ? "bg-moss" : "bg-ember-orange"}`} style={{ width: `${pct}%` }} />
            </div>
            <div className="flex items-center justify-between mt-1.5 text-xs">
                <span className="font-bold text-granite">{progress} / {target}</span>
                <span className="font-bold text-ember-orange">+{c.xp_reward} XP</span>
            </div>
        </div>
    );
}
